import React, { useEffect } from "react";
import styled from "styled-components";
import Button from "~/components-shared/Button";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useDispatch, useSelector } from "react-redux";
import eventsActionCreators from "~/store/events/actions";
import eventSelectors from "~/store/events/selectors";
import { CRUDActionEnum, AuthoritiesEnum, RoutesEnum, StatusStateEnum, EventTypeEnum } from "~/types/enums";
import Modal from "~/components-shared/Modal";
import EventForm from "~/forms/EventForm";
import eventSchema from "~/forms/EventForm/eventSchema";
import toastsActionCreators from "~/store/toasts/actions";
import { useHistory } from "react-router-dom";
import { Alert } from "~/components-shared";
import useAbacExt from "~/hooks/abac";
import authSelectors from "~/store/auth/selectors";
const Form = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  height: 100%;
`;
const EventUpdateModal = () => {
  const {
    t
  } = useTranslation();
  const user = useSelector(authSelectors.getUser);
  const event = useSelector(eventSelectors.getSelected);
  const eventId = event?.id || "";
  const status = useSelector(eventSelectors.getStatusByEvent(eventId));
  const error = useSelector(eventSelectors.getErrorByEvent(eventId));
  const history = useHistory();
  const dispatch = useDispatch();
  const {
    userHasOneOf
  } = useAbacExt();
  const actions = [];
  const defaultValues = {
    name: event?.name || "",
    description: event?.description || "",
    dateFrom: event?.dateFrom || "",
    dateTo: event?.dateTo || "",
    type: event?.type || EventTypeEnum.COMMUNITY_EVENT,
    attendees: event?.attendees ? event.attendees.map(_ref => {
      let {
        id
      } = _ref;
      return id;
    }) : [],
    communityIds: event?.communities ? event?.communities.map(_ref2 => {
      let {
        id
      } = _ref2;
      return id;
    }) : [],
    videoLink: event?.videoLink || "",
    shareInSlack: event?.shareInSlack || false,
    includeSurvey: event?.includeSurvey || false,
    googleCalendarSync: event?.googleCalendarSync || false
  };
  const {
    handleSubmit,
    control,
    reset,
    watch,
    setValue
  } = useForm({
    resolver: yupResolver(eventSchema(user)),
    defaultValues
  });
  useEffect(() => {
    if (event) {
      reset(defaultValues);
    }
  }, [eventId]);
  const onSubmit = handleSubmit(data => {
    if (eventId) {
      dispatch(eventsActionCreators.updateStarted(eventId, data, _ref3 => {
        let {
          data: newEvent
        } = _ref3;
        if (newEvent) {
          dispatch(toastsActionCreators.pushed({
            severity: "success",
            message: t("EventUpdate.eventUpdated", {
              name: newEvent.name
            }),
            timeout: 10000,
            actions: [{
              icon: "external-link",
              onClick: () => history.push(RoutesEnum.EVENT.replace(":eventId", newEvent.id))
            }]
          }));
        }
      }));
    }
  });
  const handleCancel = () => {
    dispatch(eventsActionCreators.actionChanged(undefined, undefined));
  };
  const handleDelete = () => {
    dispatch(eventsActionCreators.actionChanged(CRUDActionEnum.DELETE, event?.id));
  };
  if (userHasOneOf([AuthoritiesEnum.EVENTS_WRITE], event?.createdBy.id)) {
    actions.push({
      icon: "bin",
      tip: t("common.actions.delete"),
      onClick: handleDelete
    });
  }
  return /*#__PURE__*/React.createElement(Modal.Body, {
    title: t("EventUpdate.updateEvent"),
    onClose: handleCancel,
    actions: actions,
    maxWidth: "600px",
    fullWidth: true
  }, /*#__PURE__*/React.createElement(Form, {
    onSubmit: onSubmit
  }, /*#__PURE__*/React.createElement(Modal.Content, null, error && /*#__PURE__*/React.createElement(Alert, {
    severity: "danger",
    s: {
      mb: 3
    },
    fullWidth: true
  }, error), /*#__PURE__*/React.createElement(EventForm, {
    watch: watch,
    setValue: setValue,
    control: control
  })), /*#__PURE__*/React.createElement(Modal.Actions, null, /*#__PURE__*/React.createElement(Button, {
    color: "transparent",
    s: {
      px: 8
    },
    onClick: handleCancel,
    rounded: true
  }, t("common.actions.cancel")), /*#__PURE__*/React.createElement(Button, {
    color: "primary",
    type: "submit",
    maxWidth: "230px",
    onClick: onSubmit,
    s: {
      ml: 2
    },
    loading: status === StatusStateEnum.LOADING,
    fullWidth: true,
    rounded: true
  }, t("EventUpdate.saveEvent")))));
};
export default EventUpdateModal;